import React from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM, Text, Icon } from 'components/atoms';
import { TestExperiment as Experiment } from 'types/aiReviewTest';
import { useExperimentProgress } from 'hooks/aiReviewTest/useExperiments';

interface RunningExperimentsProps {
  experiments: Experiment[];
  onExperimentClick: (experiment: Experiment) => void;
}

interface ExperimentCardProps {
  experiment: Experiment;
  onClick: () => void;
}

const ExperimentCard: React.FC<ExperimentCardProps> = ({ experiment, onClick }) => {
  const { data: progressData } = useExperimentProgress(experiment.id);
  const progress = progressData?.data?.data;

  const calculateProgress = () => {
    if (!progress) return 0;
    const total = (progress as any).total || 0;
    const completed = (progress as any).completed || 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getElapsedTime = () => {
    if (!experiment.startedAt) return '';
    const started = new Date(experiment.startedAt);
    const now = new Date();
    const diffMs = now.getTime() - started.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffHours > 0) {
      return `${diffHours}시간 ${diffMins % 60}분 경과`;
    }
    return `${diffMins}분 경과`;
  };

  return (
    <Card onClick={onClick}>
      <CardHeader>
        <ExperimentInfo>
          <ExperimentName>{experiment.name}</ExperimentName>
          <ExperimentMeta>
            <MetaItem>
              <Icon name="Clock" size={14} />
              <MetaText>{getElapsedTime()}</MetaText>
            </MetaItem>
            <MetaItem>
              <Icon name="Hash" size={14} />
              <MetaText>#{experiment.id}</MetaText>
            </MetaItem>
          </ExperimentMeta>
        </ExperimentInfo>
        <ProgressInfo>
          <ProgressText>
            {(progress as any)?.completed || 0} / {(progress as any)?.total || 0}
          </ProgressText>
          <ProgressPercent>{calculateProgress()}%</ProgressPercent>
        </ProgressInfo>
      </CardHeader>
      
      <ProgressBarWrapper>
        <ProgressBar>
          <ProgressFill style={{ width: `${calculateProgress()}%` }} />
        </ProgressBar>
      </ProgressBarWrapper>

      {(progress as any)?.currentTask && (
        <CurrentTask>
          <TaskLabel>현재 작업:</TaskLabel>
          <TaskText>{(progress as any).currentTask}</TaskText>
        </CurrentTask>
      )}

      {(progress as any)?.failed && (progress as any).failed > 0 && (
        <ErrorInfo>
          <Icon name="Alert Circle" size={16} color={COLOR_SYSTEM.get("Red")?.[600]} />
          <ErrorText>{(progress as any).failed}개 실패</ErrorText>
        </ErrorInfo>
      )}
      
      {(progress as any)?.message && (
        <CurrentTask>
          <TaskLabel>메시지:</TaskLabel>
          <TaskText>{(progress as any).message}</TaskText>
        </CurrentTask>
      )}
    </Card>
  );
};

const RunningExperiments: React.FC<RunningExperimentsProps> = ({ experiments, onExperimentClick }) => {
  return (
    <Container>
      <SectionHeader>
        <SectionTitle>
          <Icon name="Activity" size={20} />
          실행 중인 실험
        </SectionTitle>
        <ExperimentCount>{experiments.length}개</ExperimentCount>
      </SectionHeader>

      <CardGrid>
        {experiments.map((experiment) => (
          <ExperimentCard
            key={experiment.id}
            experiment={experiment}
            onClick={() => onExperimentClick(experiment)}
          />
        ))}
      </CardGrid>
    </Container>
  );
};

const Container = styled.div`
  background: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border: 2px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 12px;
  padding: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Blue")?.[900]};
`;

const ExperimentCount = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Blue")?.[700]};
  background: ${COLOR_SYSTEM.get("Blue")?.[100]};
  padding: 4px 12px;
  border-radius: 20px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const ExperimentInfo = styled.div`
  flex: 1;
`;

const ExperimentName = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin-bottom: 4px;
`;

const ExperimentMeta = styled.div`
  display: flex;
  gap: 16px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const MetaText = styled(Text)`
  font-size: 12px;
  color: inherit;
`;

const ProgressInfo = styled.div`
  text-align: right;
`;

const ProgressText = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const ProgressPercent = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Blue")?.[600]};
`;

const ProgressBarWrapper = styled.div`
  margin-bottom: 12px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
  transition: width 0.3s ease;
`;

const CurrentTask = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 6px;
  margin-bottom: 8px;
`;

const TaskLabel = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const TaskText = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  flex: 1;
`;

const ErrorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${COLOR_SYSTEM.get("Red")?.[600]};
`;

const ErrorText = styled(Text)`
  font-size: 12px;
  color: inherit;
`;

export default RunningExperiments;